// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { Button } from '../components/ui/button';
import MfaSetupFlow from '../components/MfaSetupFlow';
import { applyTheme } from '../components/PreferencesModal';
import ConfirmModal from '../components/ConfirmModal';
import IdentiteFacturationModal from '../components/IdentiteFacturationModal';
import { authentifierPaiement } from '../lib/stripeJs';
import { gereSonAbonnement } from '../lib/acces';
import { oublierDonneesDuCompte } from '../lib/stockageSession';
import {
  changePassword, setupMfa, confirmMfaSetup, disableMfa,
  getBillingSubscription, createCheckoutSession, createPortalSession, confirmCheckoutSession,
  previewPlanChange, cancelPlanChange, abandonPendingChange, resumeSubscription,
  exportMyData, deleteAccount, getDeletionPreview, getIdentiteFacturation,
  getPreferences, setPreferences,
} from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ROLE_LABELS = {
  admin:   { label: 'Administrateur', color: '#92400e', bg: '#fef3c7' },
  dev:     { label: 'Développeur',    color: '#5b21b6', bg: '#ede9fe' },
  partner: { label: 'Partenaire',     color: '#0369a1', bg: '#e0f2fe' },
  user:    { label: 'Utilisateur',    color: 'var(--black)', bg: '#f3f4f6' },
};

const TABS = ['profile', 'security', 'billing', 'preferences', 'privacy'];

// ─── Composants locaux ────────────────────────────────────────────────────────

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
    {children}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{ background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: '24px 28px', marginBottom: 20, ...style }}>
    {children}
  </div>
);

const inp = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--gray)', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
  background: 'var(--bg-input)', color: 'var(--black)',
};

// ─── Onglet Profil ───────────────────────────────────────────────────────────

const ProfileTab = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError]     = useState('');
  const [nameSuccess, setNameSuccess] = useState('');
  const roleCfg = ROLE_LABELS[user?.role] || ROLE_LABELS.user;

  const handleSaveName = async (e) => {
    e.preventDefault();
    setNameError(''); setNameSuccess('');
    if (!displayName.trim()) { setNameError(t('err_name_empty')); return; }
    if (displayName === (user?.display_name || '')) { setNameError(t('err_no_change')); return; }
    setNameLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: displayName }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Erreur');
      setNameSuccess(t('ok_name_updated'));
      await refreshUser();
    } catch (err) { setNameError(err.message); }
    finally { setNameLoading(false); }
  };

  return (
    <>
      {/* Avatar + infos */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 24, fontWeight: 700, flexShrink: 0 }}>
            {(user?.display_name || user?.email || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)' }}>{user?.display_name || user?.email?.split('@')[0]}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{user?.email}</div>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: roleCfg.bg, color: roleCfg.color, marginTop: 4, display: 'inline-block' }}>{roleCfg.label}</span>
          </div>
        </div>

        <SectionTitle>{t('profile_display_name')}</SectionTitle>
        <form onSubmit={handleSaveName}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('profile_name_hint')}</label>
          <input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder={user?.email?.split('@')[0]} style={inp} />
          {nameError   && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>❌ {nameError}</p>}
          {nameSuccess && <p style={{ color: '#22c55e', fontSize: 12, marginTop: 6 }}>{nameSuccess}</p>}
          <Button type="submit" disabled={nameLoading} style={{ marginTop: 10 }}>
            {nameLoading ? t('saving') : t('profile_save_name')}
          </Button>
        </form>
      </Card>

      {/* Email en LECTURE SEULE depuis le 29/07/2026. Le formulaire de
          changement a été retiré : l'adresse n'était jamais vérifiée, elle
          contournait `email_allowed_domains`, et depuis « mot de passe oublié »
          la pointer vers une boîte qu'on ne possède pas ouvrait une voie de
          prise de contrôle. Détail dans `update_profile` (flask_app.py). */}
      <Card>
        <SectionTitle>{t('profile_email_section')}</SectionTitle>
        <div>
          <input type="email" value={user?.email || ''} readOnly disabled
                 style={{ ...inp, background: 'var(--bg-muted, #f3f4f6)', color: 'var(--text-muted)' }} />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            {t('profile_email_locked')}
          </p>
        </div>
      </Card>
    </>
  );
};

// ─── Onglet Sécurité ─────────────────────────────────────────────────────────

const SecurityTab = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [currentPw, setCurrentPw]     = useState('');
  const [newPw, setNewPw]             = useState('');
  const [confirmPw, setConfirmPw]     = useState('');
  const [pwLoading, setPwLoading]     = useState(false);
  const [pwError, setPwError]         = useState('');
  const [pwSuccess, setPwSuccess]     = useState('');
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaDisableMode, setMfaDisableMode] = useState(false);
  const [mfaDisablePw, setMfaDisablePw] = useState('');
  const [mfaLoading, setMfaLoading]   = useState(false);
  const [mfaError, setMfaError]       = useState('');
  const [mfaSuccess, setMfaSuccess]   = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (!currentPw || !newPw || !confirmPw) { setPwError(t('err_fields_required')); return; }
    if (newPw !== confirmPw) { setPwError(t('err_pw_mismatch')); return; }
    if (newPw.length < 8) { setPwError(t('err_pw_min')); return; }
    setPwLoading(true);
    try {
      await changePassword(currentPw, newPw);
      setPwSuccess(t('ok_pw_updated'));
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) { setPwError(err.message); }
    finally { setPwLoading(false); }
  };

  const handleDisableMfa = async (e) => {
    e.preventDefault();
    setMfaError(''); setMfaSuccess('');
    if (!mfaDisablePw) { setMfaError(t('err_pw_required')); return; }
    setMfaLoading(true);
    try {
      await disableMfa(mfaDisablePw);
      setMfaDisableMode(false);
      setMfaDisablePw('');
      setMfaSuccess(t('mfa_success_disabled'));
      await refreshUser();
    } catch (err) { setMfaError(err.message); }
    finally { setMfaLoading(false); }
  };

  return (
    <>
      <Card>
        <SectionTitle>{t('profile_pw_section')}</SectionTitle>
        <form onSubmit={handleChangePassword}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              [t('profile_old_pw'),     currentPw, setCurrentPw, 'current-password'],
              [t('profile_new_pw'),     newPw,     setNewPw,     'new-password'],
              [t('profile_confirm_pw'), confirmPw, setConfirmPw, 'new-password'],
            ].map(([label, val, setter, ac]) => (
              <div key={label}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>{label}</label>
                <input type="password" value={val} onChange={e => setter(e.target.value)} style={inp} autoComplete={ac} />
              </div>
            ))}
          </div>
          {pwError   && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>❌ {pwError}</p>}
          {pwSuccess && <p style={{ color: '#22c55e', fontSize: 12, marginTop: 8 }}>{pwSuccess}</p>}
          <Button type="submit" variant="outline" disabled={pwLoading} style={{ marginTop: 12 }}>
            {pwLoading ? t('changing_pw') : t('profile_change_pw')}
          </Button>
        </form>
      </Card>

      <Card>
        <SectionTitle>{t('mfa_section_title')}</SectionTitle>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
          background: user?.mfa_enabled ? '#f0fdf4' : '#f3f4f6',
          color: user?.mfa_enabled ? '#16a34a' : 'var(--text-muted)',
          display: 'inline-block', marginBottom: 12,
        }}>
          {user?.mfa_enabled ? t('mfa_status_enabled') : t('mfa_status_disabled')}
        </span>

        {!user?.mfa_enabled && !mfaDisableMode && (
          <Button onClick={() => setMfaSetupOpen(true)} variant="outline" style={{ display: 'block' }}>
            {t('mfa_enable_cta')}
          </Button>
        )}
        {user?.mfa_enabled && !mfaDisableMode && (
          <Button onClick={() => setMfaDisableMode(true)} variant="outline" style={{ display: 'block' }}>
            {t('mfa_disable_cta')}
          </Button>
        )}
        {mfaDisableMode && (
          <form onSubmit={handleDisableMfa}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('mfa_disable_password_label')}</label>
            <input type="password" value={mfaDisablePw} onChange={e => setMfaDisablePw(e.target.value)} style={inp} autoComplete="current-password" />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Button type="button" variant="outline" onClick={() => { setMfaDisableMode(false); setMfaDisablePw(''); setMfaError(''); }} style={{ flex: 1 }}>{t('cancel')}</Button>
              <Button type="submit" variant="destructive" disabled={mfaLoading} style={{ flex: 1 }}>{mfaLoading ? t('mfa_verifying') : t('mfa_disable_cta')}</Button>
            </div>
          </form>
        )}
        {mfaError   && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>❌ {mfaError}</p>}
        {mfaSuccess && <p style={{ color: '#22c55e', fontSize: 12, marginTop: 8 }}>{mfaSuccess}</p>}
      </Card>

      {mfaSetupOpen && (
        <MfaSetupFlow
          onSetup={setupMfa}
          onConfirm={confirmMfaSetup}
          onClose={() => setMfaSetupOpen(false)}
          onComplete={async () => {
            setMfaSetupOpen(false);
            setMfaSuccess(t('mfa_success_enabled'));
            await refreshUser();
          }}
        />
      )}
    </>
  );
};

// ─── Onglet Mes données (RGPD Art. 20 et Art. 17) ────────────────────────────
//
// Ces deux gestes existaient côté serveur depuis avril 2026 et n'avaient AUCUNE
// interface : ils vivaient dans `pages/Account.js`, page jamais routée dans
// `App.js`. Portés ici le 5/08/2026 plutôt que de ressusciter ce fichier, qui
// était intégralement en français en dur — zéro `t()` — et dupliquait des
// sections déjà tenues par cet écran.
//
// ⚠️ La suppression n'est pas proposée à un siège de licence : le serveur la
// refuse (403 `license_seat`) parce que le compte est prêté par l'employeur et
// que les rapports lui appartiennent. Afficher un bouton qui échoue à tous les
// coups n'aide personne — on explique à qui s'adresser. La serrure reste côté
// serveur, ceci n'est que de la courtoisie.

const PrivacyTab = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError]     = useState('');
  const [exportDone, setExportDone]       = useState(false);
  const [confirmText, setConfirmText]     = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError]     = useState('');
  // 0 = au repos · 1 = « voulez-vous vraiment ? » · 2 = récapitulatif chiffré
  // · 3 = saisie de la phrase + confirmation finale.
  const [etape, setEtape]                 = useState(0);
  const [apercu, setApercu]               = useState(null);

  const phrase = t('rgpd_delete_confirm_phrase');
  // ⚠️ Un titulaire Cabinet porte un `license_id` sans être un siège : il a payé
  // l'abonnement qui a ouvert la licence. Le test brut sur `license_id` lui
  // opposait « adressez-vous à l'administrateur de votre licence » — or c'est
  // lui. Son droit à l'effacement (art. 17) devenait inexerçable, avec pour
  // seule issue un message le renvoyant à lui-même. Même règle partagée que
  // pour l'onglet « Abonnement » : `lib/acces.js`.
  const estSiegeLicence = !gereSonAbonnement(user);

  // A-t-il un abonnement payant en cours ? La suppression le résiliera, et il
  // doit pouvoir choisir la porte de sortie la moins définitive.
  const aUnAbonnement = gereSonAbonnement(user) && !!user?.tier_code;

  const handleExport = async () => {
    setExportError(''); setExportDone(false); setExportLoading(true);
    try {
      await exportMyData();
      setExportDone(true);
    } catch (err) { setExportError(err.message); }
    finally { setExportLoading(false); }
  };

  // L'aperçu est chargé au passage à l'étape 2, pas à l'ouverture de l'onglet :
  // inutile d'interroger le serveur pour quelqu'un qui ne fait que consulter ses
  // paramètres. En cas d'échec on avance quand même — un compteur indisponible
  // ne doit pas empêcher d'exercer un droit, le texte d'avertissement reste.
  const allerEtape2 = async () => {
    setDeleteError('');
    setEtape(2);
    try {
      setApercu(await getDeletionPreview());
    } catch (_) {
      setApercu(null);
    }
  };

  const annuler = () => {
    setEtape(0); setApercu(null); setConfirmText(''); setDeleteError('');
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleteError('');
    if (confirmText !== phrase) return;
    setDeleteLoading(true);
    try {
      await deleteAccount(confirmText);
      // Le compte n'existe plus : le serveur a effacé les cookies de session en
      // répondant, et ses traces locales partent aussi.
      oublierDonneesDuCompte();
      window.location.href = '/login';
    } catch (err) {
      setDeleteError(err.message);
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Card>
        <SectionTitle>{t('rgpd_export_title')}</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          {t('rgpd_export_intro')}
        </p>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          {t('rgpd_export_note')}
        </p>
        <Button onClick={handleExport} variant="outline" disabled={exportLoading}>
          {exportLoading ? t('rgpd_export_loading') : t('rgpd_export_cta')}
        </Button>
        {exportError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>❌ {exportError}</p>}
        {exportDone  && <p style={{ color: '#22c55e', fontSize: 12, marginTop: 8 }}>{t('rgpd_export_done')}</p>}
      </Card>

      {estSiegeLicence ? (
        <Card>
          <SectionTitle>{t('rgpd_delete_licence_title')}</SectionTitle>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {t('rgpd_delete_licence_intro')}
          </p>
        </Card>
      ) : (
        <Card style={{ border: '2px solid #fecaca' }}>
          <SectionTitle>{t('rgpd_delete_title')}</SectionTitle>

          {/* Parcours en 3 étapes. Chacune apporte une information NOUVELLE :
              répéter « êtes-vous sûr ? » trois fois n'entraîne que le réflexe de
              cliquer, ce qui protège moins qu'une seule bonne question. */}

          {etape === 0 && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                {t('rgpd_delete_warning')}
              </p>

              {/* ⚠️ LA SORTIE LA MOINS DÉFINITIVE, PROPOSÉE EN PREMIER.
                  C'est la séquence que recommande la pratique du marché
                  (résilier → exporter → supprimer) : beaucoup de gens qui
                  cliquent « supprimer mon compte » veulent en réalité arrêter
                  d'être prélevés, pas perdre leurs rapports. On le leur demande
                  avant, pendant que c'est encore réversible — une résiliation
                  se reprend d'un clic, un effacement jamais.

                  ⛔ PROPOSÉ, JAMAIS IMPOSÉ. Conditionner la suppression à une
                  résiliation préalable ferait obstacle à un droit : l'article 17
                  ne prévoit que six exceptions (obligation légale, liberté
                  d'expression, santé publique, archives, défense en justice,
                  mission publique) et un abonnement en cours n'en est pas une.
                  Le bouton « supprimer » reste donc accessible à côté, et le
                  serveur résilie tout seul si la personne passe outre. */}
              {aUnAbonnement && (
                <div style={{
                  border: '1.5px solid #bfdbfe', borderRadius: 10,
                  background: '#eff6ff', padding: '12px 14px',
                  fontSize: 13, color: 'var(--gray-dark)', marginBottom: 16,
                }}>
                  <div style={{ fontWeight: 600, color: 'var(--black)', marginBottom: 4 }}>
                    {t('rgpd_delete_sub_offer_title')}
                  </div>
                  <div style={{ marginBottom: 10 }}>{t('rgpd_delete_sub_offer_body')}</div>
                  <Button size="sm"
                          onClick={() => { window.location.href = '/parametres?tab=billing'; }}>
                    {t('rgpd_delete_sub_offer_cta')}
                  </Button>
                </div>
              )}

              <Button variant="destructive" onClick={() => setEtape(1)}>
                {t('rgpd_delete_step1_cta')}
              </Button>
            </>
          )}

          {etape === 1 && (
            <>
              <p style={{ fontSize: 14, color: 'var(--black)', fontWeight: 600, marginBottom: 16 }}>
                {t('rgpd_delete_step1_question')}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="outline" onClick={annuler}>{t('cancel')}</Button>
                <Button variant="destructive" onClick={allerEtape2}>
                  {t('rgpd_delete_step1_continue')}
                </Button>
              </div>
            </>
          )}

          {etape === 2 && (
            <>
              <p style={{ fontSize: 14, color: 'var(--black)', fontWeight: 600, marginBottom: 12 }}>
                {t('rgpd_delete_step2_title')}
              </p>
              {apercu && (
                <ul style={{ fontSize: 13, color: 'var(--black)', marginBottom: 12, paddingLeft: 20 }}>
                  {[
                    ['analyses',    t('rgpd_delete_step2_analyses')],
                    ['projets',     t('rgpd_delete_step2_projets')],
                    ['documents',   t('rgpd_delete_step2_documents')],
                    ['annotations', t('rgpd_delete_step2_annotations')],
                    ['notes',       t('rgpd_delete_step2_notes')],
                    // Révision 0011. ⚠️ Cette liste est ÉNUMÉRÉE, pas dérivée des
                    // clés rendues par l'API : une table ajoutée au décompte mais
                    // oubliée ici disparaîtrait de l'écran de confirmation sans
                    // que rien ne le signale — et l'écran annoncerait moins que
                    // ce qu'il détruit.
                    ['retours',     t('rgpd_delete_step2_retours')],
                  ].filter(([cle]) => apercu[cle] > 0).map(([cle, libelle]) => (
                    <li key={cle} style={{ marginBottom: 2 }}>
                      <strong>{apercu[cle]}</strong> {libelle}
                    </li>
                  ))}
                </ul>
              )}
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                {t('rgpd_delete_step2_files')}
              </p>
              <p style={{ fontSize: 13, color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>
                {t('rgpd_delete_step2_irreversible')}
              </p>

              {/* ⚠️ CE QUI ARRIVE EN PLUS DE L'EFFACEMENT DES DONNÉES.
                  L'abonnement sera résilié sur-le-champ : on ne découvre pas
                  qu'on a mis fin à un contrat après avoir tapé « SUPPRIMER MON
                  COMPTE ». Le prorata non consommé n'est pas remboursé — le
                  dire ici, et pas dans les CGV seulement. */}
              {apercu?.abonnement_resilie && (
                <div style={{
                  border: '1.5px solid #fcd34d', borderRadius: 10,
                  background: 'rgba(245,158,11,0.09)', padding: '10px 12px',
                  fontSize: 13, lineHeight: 1.55, color: 'var(--gray-dark)',
                  marginBottom: 12,
                }}>
                  <div style={{ fontWeight: 600, color: 'var(--black)', marginBottom: 2 }}>
                    {t('rgpd_delete_sub_cancelled_title')}
                  </div>
                  {t('rgpd_delete_sub_cancelled_body')}
                  {/* Quitter Cabinet met aussi des collègues dehors — la seule
                      conséquence qui touche d'autres personnes. */}
                  {apercu.sieges_perdus > 0 && (
                    <div style={{ marginTop: 6, color: '#dc2626', fontWeight: 600 }}>
                      <strong>{apercu.sieges_perdus}</strong>{' '}
                      {apercu.sieges_perdus > 1
                        ? t('rgpd_delete_seats_lost_plural')
                        : t('rgpd_delete_seats_lost_singular')}
                    </div>
                  )}
                </div>
              )}
              {/* Rappel de l'export uniquement si la personne ne l'a pas fait
                  dans cette session — sinon c'est du bruit, et le bruit fait
                  survoler l'écran suivant. */}
              {!exportDone && (
                <p style={{ fontSize: 13, color: 'var(--black)', fontWeight: 600, marginBottom: 16 }}>
                  {t('rgpd_delete_step2_export_hint')}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="outline" onClick={annuler}>{t('rgpd_delete_back')}</Button>
                <Button variant="destructive" onClick={() => setEtape(3)}>
                  {t('rgpd_delete_step2_continue')}
                </Button>
              </div>
            </>
          )}

          {etape === 3 && (
            <>
              <p style={{ fontSize: 14, color: 'var(--black)', fontWeight: 600, marginBottom: 8 }}>
                {t('rgpd_delete_step3_title')}
              </p>
              <p style={{ fontSize: 13, color: '#dc2626', marginBottom: 16 }}>
                {t('rgpd_delete_step3_question')}
              </p>
              <form onSubmit={handleDelete}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  {t('rgpd_delete_confirm_label')} <strong>{phrase}</strong> {t('rgpd_delete_confirm_hint')}
                </label>
                <input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={phrase}
                  style={{ ...inp, border: '1px solid #fecaca' }}
                />
                {deleteError && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>❌ {deleteError}</p>}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <Button type="button" variant="outline" onClick={annuler}>
                    {t('rgpd_delete_back')}
                  </Button>
                  <Button type="submit" variant="destructive"
                          disabled={deleteLoading || confirmText !== phrase}>
                    {deleteLoading ? t('rgpd_delete_loading') : t('rgpd_delete_cta')}
                  </Button>
                </div>
              </form>
            </>
          )}
        </Card>
      )}

      <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
        {t('rgpd_contact_note')}
      </p>
    </>
  );
};

// ─── Onglet Abonnement ───────────────────────────────────────────────────────

// Libellés commerciaux des trois paliers. ⚠️ Source unique du fichier : le
// bandeau « plan actuel » les lit aussi. `subData.plans` ne porte PAS
// `nom_affiche` — la route /api/billing/subscription ne l'expose pas.
const LIBELLE_PALIER = { ESSENTIEL: 'Essentiel', PRO: 'Pro', CABINET: 'Cabinet' };

// Le palier mis en avant : PRO depuis la grille du 05/09/2026 (auparavant PRO+).
const PALIER_VEDETTE = 'PRO';

// Ce que chaque palier AJOUTE au précédent (24/09/2026). La carte Pro dit
// « Tout le forfait Essentiel, plus : » puis ses seuls ajouts — la grille à
// trois paliers (`474a8e8`) avait perdu cette ligne, si bien que l'OCR, affiché
// sur Essentiel seulement, semblait absent de Pro et de Cabinet.
// ⚠️ Chaque ligne doit être VRAIE dans le code, pas seulement au catalogue :
//   Projets        ⇐ `pricing_tiers.projet_disponible`
//   certification  ⇐ `PALIERS_VERIFICATION_CERTIFICATION` (core/droits.py, lib/acces.js)
//   statistiques   ⇐ ouvertes à TOUS (`/api/stats/me`) : ligne d'Essentiel, pas de Pro.
// ⚠️ Aucun nombre d'analyses ici : il vient de `pricing_tiers` (au-dessus de la liste).
const FEATURES_PALIER = {
  ESSENTIEL: { base: null,
               ajouts: ['billing_feat_ocr', 'billing_feat_rapport', 'billing_feat_stats',
                        'billing_feat_support'] },
  PRO:       { base: 'ESSENTIEL',
               ajouts: ['billing_feat_projets', 'billing_feat_certification'] },
  CABINET:   { base: 'PRO',
               ajouts: ['billing_feat_sieges', 'billing_feat_pool', 'billing_feat_gestion',
                        'billing_feat_vue_equipe'] },
};

// ⛔ Plus de choix mensuel/annuel : les forfaits sont mensuels uniquement
// depuis le 08/09/2026 (trésorerie — cf `core/config.py`). La licence
// Entreprise reste annuelle, mais elle ne se souscrit pas depuis cet écran.
// TROIS états possibles pour une carte, et non deux (11/09/2026) :
//
//   `actuel`    — le forfait en cours, rien de programmé  → vert
//   `sortant`   — le forfait en cours, qui SE TERMINE      → orange
//   `entrant`   — le forfait qui PRENDRA EFFET             → vert, en pulsation
//
// Sans le troisième, un client ayant programmé une rétrogradation voyait sa
// carte inchangée et ne pouvait plus revenir en arrière. Le vocabulaire
// « se termine le » / « commencera le » est celui du marché (Notion, Zoom).
const PlanCard = ({ tier, isCurrentPlan, billing, onSubscribe, loading,
                   etat = 'actuel', dateBascule, onKeepPlan }) => {
  const { t } = useI18n();
  const isVedette = tier === PALIER_VEDETTE;
  const price     = billing?.amount_monthly;
  const priceId   = billing?.price_monthly;

  const { base, ajouts: features = [] } = FEATURES_PALIER[tier] || {};
  // `{n}` des sièges : le nombre vient de `pricing_tiers.sieges_inclus`.
  const libelleAvantage = (key) =>
    key === 'billing_feat_sieges' ? t(key).replace('{n}', billing?.sieges ?? '') : t(key);

  const accent = 'var(--blue)';
  const VERT = '#16a34a';
  const ORANGE = '#f59e0b';

  const estSortant = etat === 'sortant';
  const estEntrant = etat === 'entrant';
  const couleurEtat = estSortant ? ORANGE : VERT;

  return (
    // ⚠️ Le forfait souscrit et le forfait mis en avant ne doivent PAS porter la
    // même couleur : tous deux étaient bleus, si bien qu'on ne distinguait plus
    // celui qu'on avait payé de celui qu'on nous proposait.
    <div
      className={estEntrant ? 'carte-forfait-entrant' : undefined}
      style={{
        border: `2px solid ${isCurrentPlan || estEntrant ? couleurEtat : (isVedette ? accent : 'var(--gray)')}`,
        borderRadius: 12, padding: '24px 22px',
        background: isCurrentPlan ? (estSortant ? 'rgba(245,158,11,0.06)' : 'rgba(22,163,74,0.05)')
                                  : (estEntrant ? 'rgba(22,163,74,0.05)' : 'var(--bg-surface)'),
        position: 'relative', flex: 1, minWidth: 0,
      }}>
      {(isCurrentPlan || estEntrant) && (
        <span style={{
          position: 'absolute', top: -11, left: 18,
          fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99,
          background: couleurEtat, color: 'white',
        }}>
          {estSortant ? t('billing_badge_ending')
            : estEntrant ? t('billing_badge_starting')
            : t('billing_current_badge')}
        </span>
      )}
      {/* La date de bascule, sur les DEUX cartes concernées : c'est elle qui
          rend la situation lisible d'un coup d'œil. */}
      {(estSortant || estEntrant) && dateBascule && (
        <div style={{
          fontSize: 12, fontWeight: 600, color: couleurEtat, marginBottom: 8,
        }}>
          {estSortant ? t('billing_ends_on') : t('billing_starts_on')} {dateBascule}
        </div>
      )}
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>
        {LIBELLE_PALIER[tier] || tier}
      </div>
      {price != null ? (
        <div style={{ marginBottom: 16 }}>
          <div>
            <span style={{ fontSize: 30, fontWeight: 800, color: accent }}>{price.toFixed(2).replace('.', ',')} €</span>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}> {t('billing_excl_tax')} {t('billing_per_month')}</span>
          </div>
          {/* Ce que coûte le dépassement, collé au prix : sans lui, la
              facturation à l'unité restait invisible avant la souscription.
              Le nombre d'analyses INCLUSES est descendu dans la liste du bas,
              en tête et en gras (Marc, 24/09/2026) : en petit gris sous le prix,
              on ne le voyait pas. */}
          {billing?.prix_supplementaire != null && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              {t('billing_analyse_sup')} : {billing.prix_supplementaire.toFixed(2).replace('.', ',')} € {t('billing_excl_tax')}
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: 16, height: 38 }} />
      )}

      {/* En TÊTE de liste : propre à chaque palier (pas un héritage), et le
          premier critère de choix d'un forfait. Lu dans `pricing_tiers`. */}
      {billing?.analyses_incluses != null && (
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)', display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
          <span style={{ color: '#22c55e', flexShrink: 0, marginTop: 1 }}>✓</span>
          {billing.analyses_incluses} {t('billing_analyses_incluses')}
        </div>
      )}
      {base && (
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
          {t('billing_tout_plus').replace('{palier}', LIBELLE_PALIER[base])}
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {features.map(key => (
          <li key={key} style={{ fontSize: 13, color: 'var(--black)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ color: '#22c55e', flexShrink: 0, marginTop: 1 }}>✓</span>
            {libelleAvantage(key)}
          </li>
        ))}
      </ul>

      {/* ⚠️ Sur la carte SORTANTE, le bouton fait revenir en arrière : c'est le
          seul moyen d'annuler une rétrogradation depuis Normascan. Libellé
          « Conserver ce forfait » et non « Annuler » — ce dernier se lit
          « résilier mon abonnement », exactement le contraire. */}
      {estSortant ? (
        <Button
          onClick={onKeepPlan}
          disabled={loading}
          style={{ width: '100%' }}
        >
          {loading ? t('billing_loading') : t('billing_keep_plan_cta')}
        </Button>
      ) : estEntrant ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', paddingTop: 8 }}>
          {t('billing_badge_starting')}
        </div>
      ) : !isCurrentPlan && priceId ? (
        <Button
          onClick={() => onSubscribe(priceId)}
          disabled={loading}
          style={{ width: '100%' }}
        >
          {loading ? t('billing_redirecting') : t('billing_subscribe_cta')}
        </Button>
      ) : isCurrentPlan ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', paddingTop: 8 }}>
          {t('billing_current_badge')}
        </div>
      ) : (
        <Button disabled style={{ width: '100%', opacity: 0.4 }}>
          {t('billing_subscribe_cta')}
        </Button>
      )}
    </div>
  );
};

const BillingTab = () => {
  const { t } = useI18n();
  // ⚠️ `refreshUser` est indispensable après un changement de palier : sans lui,
  // le solde d'analyses et le bandeau resteraient sur l'ANCIEN forfait jusqu'au
  // prochain rechargement complet de la page.
  const { refreshUser, user } = useAuth();
  // Changement de forfait en attente de validation : porte le montant que
  // Stripe prélèvera, pour que le client sache avant de confirmer.
  const [confirmation, setConfirmation] = useState(null);
  // Identité de facturation (chantier SaaS 1) : `identite` vient du serveur ;
  // `pageIdentite` ouvre la fenêtre, avec le prix à payer ensuite (`priceId`),
  // ou sans (bandeau d'un abonné existant).
  const [identite, setIdentite] = useState(null);
  const [pageIdentite, setPageIdentite] = useState(null);
  const [subData, setSubData]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [subLoading, setSubLoading] = useState(false);
  const [banner, setBanner]     = useState(null);
  const [bannerType, setBannerType] = useState('success');

  // ⚠️ Par une référence : `refreshUser` est recréée à chaque rendu du
  // contexte, la mettre dans les dépendances de `loadSubscription` ferait
  // boucler le chargement.
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;

  const loadSubscription = useCallback(async () => {
    try {
      const data = await getBillingSubscription();
      setSubData(data);
      // Le BANDEAU rouge (`access_warning`) vient du profil, pas de cette
      // route : sans ce rafraîchissement il restait sur l'état d'avant — un
      // client qui venait de payer lisait « abonnement résilié » au-dessus de
      // « Abonnement activé » (test du 22/09/2026). La lecture de l'abonnement
      // peut aussi CHANGER l'état côté serveur (filet de resynchronisation).
      refreshUserRef.current?.();
    } catch (e) {
      console.error('Erreur chargement abonnement:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Détecter retour depuis Stripe
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') {
      setBanner(t('billing_stripe_success')); setBannerType('success');
      const sessionId = params.get('session_id');
      window.history.replaceState({}, '', '/parametres?tab=billing');
      // L'abonnement est appliqué PAR LE SERVEUR à partir de la session
      // Stripe, sans attendre le webhook ; puis on relit. Si la confirmation
      // échoue, le webhook reste le filet : seconde lecture après 2 s.
      (sessionId ? confirmCheckoutSession(sessionId) : Promise.resolve())
        .catch((e) => console.error('Confirmation Checkout :', e))
        .finally(() => {
          loadSubscription();
          setTimeout(loadSubscription, 2000);
        });
      return;
    }
    loadSubscription();
    if (params.get('stripe') === 'cancel') {
      setBanner(t('billing_stripe_cancel')); setBannerType('info');
      window.history.replaceState({}, '', '/parametres?tab=billing');
    }
  }, [loadSubscription, t]);

  // L'identité est lue une fois : elle décide du bandeau et de l'étape d'avant
  // paiement. Un échec de lecture n'empêche rien — le serveur reste la serrure.
  useEffect(() => {
    getIdentiteFacturation().then(setIdentite).catch(() => setIdentite(null));
  }, []);

  // Vers la page de paiement Stripe. ⚠️ Le 409 `identite_requise` ouvre la page
  // d'identité : c'est le filet si l'écran a cru l'identité complète à tort.
  const allerAuPaiement = async (priceId) => {
    try {
      const reponse = await createCheckoutSession(priceId);
      window.location.href = reponse.checkout_url;
    } catch (e) {
      if (e.code === 'identite_requise') {
        setPageIdentite({ priceId });
      } else if (e.code === 'plafond_sieges_self_service') {
        // CGV 1.4, art. 3.5 : le motif du serveur oriente vers la licence Entreprise.
        setBanner(e.message); setBannerType('error');
      } else {
        setBanner(t('billing_error')); setBannerType('error');
      }
      setSubLoading(false);
    }
  };

  // La fenêtre d'identité est validée : on enregistre, puis on paie s'il y a
  // un forfait en attente, sinon on remercie (bandeau d'un abonné existant).
  const identiteValidee = async (nouvelle) => {
    const priceId = pageIdentite?.priceId;
    setIdentite(i => ({ ...(i || {}), concerne: true, identite: nouvelle, complete: true }));
    setPageIdentite(null);
    if (priceId) {
      setSubLoading(true);
      await allerAuPaiement(priceId);
    } else {
      setBanner(t('identite_enregistree')); setBannerType('success');
    }
  };

  // ⚠️ Un changement de forfait MODIFIE UN PRÉLÈVEMENT : il ne doit jamais
  // partir sur un simple clic. On demande d'abord à Stripe ce qu'il coûtera,
  // puis on le fait confirmer avec ce montant sous les yeux.
  const handleSubscribe = async (priceId) => {
    setSubLoading(true);
    try {
      const apercu = await previewPlanChange(priceId);
      if (apercu.premiere_souscription) {
        // Aucun abonnement en cours : d'abord l'identité de facturation
        // (SIREN obligatoire, chantier SaaS 1), puis la page de paiement
        // Stripe, qui affichera le montant.
        if (identite?.concerne && !identite?.complete) {
          setPageIdentite({ priceId });
          setSubLoading(false);
          return;
        }
        await allerAuPaiement(priceId);
        return;
      }
      if (apercu.aucun_changement) {
        setBanner(t('billing_plan_unchanged')); setBannerType('info');
        setSubLoading(false);
        return;
      }
      setConfirmation({ priceId, ...apercu });
      setSubLoading(false);
    } catch (e) {
      setBanner(t('billing_error')); setBannerType('error');
      setSubLoading(false);
    }
  };

  // Reprise d'un paiement resté en attente. Même principe que la souscription :
  // on authentifie DANS l'application, et renoncer solde la demande.
  const handleFinaliserPaiement = async () => {
    if (!enAttente) return;
    setSubLoading(true);
    try {
      if (enAttente.client_secret && subData?.stripe_publishable_key) {
        const issue = await authentifierPaiement(
          subData.stripe_publishable_key, enAttente.client_secret);
        if (issue.ok) {
          setBanner(t('billing_plan_changed')); setBannerType('success');
        } else if (issue.annule) {
          setBanner(t('billing_pending_body')); setBannerType('info');
        } else {
          setBanner(t('billing_payment_required')); setBannerType('error');
        }
        await loadSubscription();
        await refreshUser();
      } else if (enAttente.url_paiement) {
        // Repli quand il n'y a rien à authentifier (carte refusée) : la page
        // Stripe permet d'en saisir une autre.
        window.open(enAttente.url_paiement, '_blank', 'noopener');
      }
    } catch (e) {
      setBanner(t('billing_error')); setBannerType('error');
    } finally {
      setSubLoading(false);
    }
  };

  // Le client renonce à une montée en gamme qu'il n'a pas payée.
  const handleAbandonPending = async () => {
    setSubLoading(true);
    try {
      await abandonPendingChange();
      setBanner(t('billing_pending_abandoned')); setBannerType('success');
      await loadSubscription();
      await refreshUser();
    } catch (e) {
      setBanner(t('billing_error')); setBannerType('error');
    } finally {
      setSubLoading(false);
    }
  };

  // Le client revient sur sa décision : la bascule programmée est annulée et il
  // conserve son forfait. Rien n'est prélevé, rien n'est remboursé.
  const handleKeepPlan = async () => {
    setSubLoading(true);
    try {
      await cancelPlanChange();
      setBanner(t('billing_plan_kept')); setBannerType('success');
      await loadSubscription();
      await refreshUser();
    } catch (e) {
      setBanner(t('billing_error')); setBannerType('error');
    } finally {
      setSubLoading(false);
    }
  };

  // Le client revient sur sa résiliation — le plus souvent faite depuis le
  // portail Stripe. Rien n'est prélevé : l'abonnement n'a jamais cessé, on
  // retire seulement la borne posée sur sa fin.
  const handleResume = async () => {
    setSubLoading(true);
    try {
      await resumeSubscription();
      setBanner(t('billing_resumed')); setBannerType('success');
      await loadSubscription();
      await refreshUser();
    } catch (e) {
      setBanner(t('billing_error')); setBannerType('error');
    } finally {
      setSubLoading(false);
    }
  };

  const appliquerChangement = async () => {
    if (!confirmation) return;
    const { priceId } = confirmation;
    setConfirmation(null);
    setSubLoading(true);
    try {
      const reponse = await createCheckoutSession(priceId);
      // ⚠️ La banque demande une authentification (3-D Secure) ou a refusé la
      // carte : le forfait n'a PAS changé. Le client doit finir son paiement
      // sur la page Stripe, sinon il croirait avoir changé d'offre.
      if (reponse.paiement_requis) {
        // ⚠️ Authentification EN PLACE, dans Normascan. La page de facture
        // hébergée n'a aucun bouton retour : le client qui renonçait devait
        // fermer son onglet, et le changement restait en attente jusqu'à
        // expiration. Ici, fermer la fenêtre de la banque revient à renoncer,
        // et on le lui propose aussitôt (constaté le 11/09/2026).
        if (reponse.client_secret && reponse.stripe_publishable_key) {
          const issue = await authentifierPaiement(
            reponse.stripe_publishable_key, reponse.client_secret);

          if (issue.ok) {
            setBanner(t('billing_plan_changed')); setBannerType('success');
          } else if (issue.annule) {
            // Il a renoncé : on solde la demande immédiatement plutôt que de
            // la laisser traîner 23 heures.
            await abandonPendingChange().catch(() => {});
            setBanner(t('billing_pending_abandoned')); setBannerType('info');
          } else {
            setBanner(t('billing_payment_required')); setBannerType('error');
          }
          await loadSubscription();
          await refreshUser();
          setSubLoading(false);
          return;
        }
        // Repli : carte refusée (rien à authentifier) ou secret indisponible.
        // Le bandeau « changement en attente » prend alors le relais, avec ses
        // deux issues.
        setBanner(t('billing_payment_required')); setBannerType('error');
        await loadSubscription();
        setSubLoading(false);
        return;
      }
      // Le serveur a modifié l'abonnement EXISTANT. Repasser par Checkout
      // créerait un second abonnement et facturerait deux fois.
      if (reponse.plan_change) {
        setBanner(t(reponse.differe ? 'billing_plan_scheduled' : 'billing_plan_changed'));
        setBannerType('success');
        await loadSubscription();
        await refreshUser();
        setSubLoading(false);
        return;
      }
      window.location.href = reponse.checkout_url;
    } catch (e) {
      // Plafond de sièges (CGV 1.4, art. 3.5) : le motif du serveur dit quoi faire.
      setBanner(e.code === 'plafond_sieges_self_service' ? e.message : t('billing_error'));
      setBannerType('error');
      setSubLoading(false);
    }
  };

  const handlePortal = async () => {
    setSubLoading(true);
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (e) {
      setBanner(t('billing_error')); setBannerType('error');
      setSubLoading(false);
    }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

  // Montée en gamme suspendue faute de paiement (authentification abandonnée
  // ou carte refusée). Le palier N'A PAS changé.
  const enAttente = subData?.paiement_en_attente || null;

  // ⚠️ Résiliation en cours — presque toujours demandée depuis le portail
  // Stripe, où Normascan ne voit rien passer. Le statut reste `active` : le
  // client garde son accès jusqu'au terme payé. Sans cette lecture, l'écran
  // affichait son forfait parfaitement intact, et lui laissait même en changer
  // — ce qui ressuscitait l'abonnement qu'il venait d'arrêter.
  const resiliation = subData?.resiliation || null;
  const dateFin = resiliation?.a_partir_de ? formatDate(resiliation.a_partir_de) : null;

  // Bascule de palier programmée (rétrogradation différée), le cas échéant.
  const programme = subData?.changement_programme || null;
  const dateBascule = programme?.a_partir_de ? formatDate(programme.a_partir_de) : null;

  // ⚠️ Une carte n'est « sortante » que si une bascule est RÉELLEMENT planifiée
  // ET qu'elle vise un AUTRE palier : sans ce second contrôle, le forfait
  // courant s'afficherait « se termine le » alors que rien ne change.
  const etatDeLaCarte = (tier) => {
    if (!programme || programme.tier_vise === tierCode) return 'actuel';
    if (tier === tierCode) return 'sortant';
    if (tier === programme.tier_vise) return 'entrant';
    return 'actuel';
  };

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{t('billing_loading')}</p>;

  const stripeConfigured = !!(subData?.plans?.PRO?.price_monthly);
  const status          = subData?.subscription_status || 'none';
  const tierCode        = subData?.tier_code;
  // ⚠️ `past_due` EN FAIT PARTIE : un client dont un paiement a échoué CONSERVE
  // son forfait pendant les relances de Stripe. L'exclure affichait « aucun
  // abonnement actif » et remettait « Souscrire » sur sa propre offre — alors
  // qu'il l'a bien, et qu'il doit seulement régulariser (constaté le
  // 11/09/2026). Le retard est signalé par le bandeau, pas en niant le forfait.
  const hasActiveSubcription = ['active', 'trialing', 'past_due'].includes(status);

  const statusLabel = {
    active:   t('billing_status_active'),
    trialing: t('billing_status_active'),
    past_due: t('billing_status_past_due'),
    canceled: t('billing_status_canceled'),
    none:     t('billing_status_none'),
  }[status] || status;

  const statusColor = {
    active:   '#22c55e',
    trialing: '#22c55e',
    past_due: '#f59e0b',
    canceled: '#ef4444',
    none:     'var(--text-muted)',
  }[status] || 'var(--text-muted)';

  return (
    <>
      {/* Confirmation d'un changement de forfait. Le montant affiché vient de
          Stripe, pas d'un calcul local : c'est celui qui sera prélevé. */}
      {confirmation && (
        <ConfirmModal
          danger={false}
          onCancel={() => setConfirmation(null)}
          onConfirm={appliquerChangement}
          message={
            <>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                {LIBELLE_PALIER[confirmation.tier_actuel] || confirmation.tier_actuel}
                {' → '}
                {LIBELLE_PALIER[confirmation.tier_vise] || confirmation.tier_vise}
              </div>
              {confirmation.differe ? (
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  {t('billing_confirm_downgrade')}
                  {subData?.subscription_end_at && (
                    <> {formatDate(subData.subscription_end_at)}</>
                  )}
                  {' '}{t('billing_confirm_downgrade_suite')}{' '}
                  <strong>{LIBELLE_PALIER[confirmation.tier_vise] || confirmation.tier_vise}</strong>.
                </div>
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  {/* ⚠️ UN SOLDE EN FAVEUR DU CLIENT NE SE PRÉLÈVE PAS : Stripe
                      le porte à son avoir et le déduira de la facture suivante.
                      Annoncer « 0,00 € sera prélevé » était exact mais taisait
                      le crédit — et laissait trois montants qui ne
                      s'additionnaient pas (16/09/2026). */}
                  {confirmation.avoir_eur ? (
                    t('billing_confirm_upgrade_avoir').replace(
                      '{montant}', confirmation.avoir_eur.toFixed(2).replace('.', ','))
                  ) : (
                    <>
                      {t('billing_confirm_upgrade')}{' '}
                      <strong>
                        {(confirmation.montant_immediat || 0).toFixed(2).replace('.', ',')} € {t('billing_incl_tax')}
                      </strong>{' '}
                      {t('billing_confirm_upgrade_suite')}
                    </>
                  )}
                  {/* Détail HT / TVA (décision du 14/09/2026) : les cartes
                      affichent des prix HT, le prélèvement est TTC. Les trois
                      montants viennent de Stripe, aucun n'est recalculé ici.
                      Le taux n'est affiché que si une TVA est réellement
                      collectée : sinon `taux_tva` vaut null, et « TVA : 0,00 € »
                      signale une configuration Stripe Tax manquante. */}
                  {confirmation.montant_ht != null && (
                    <table style={{ marginTop: 10, fontSize: 13, borderCollapse: 'collapse' }}>
                      <tbody>
                        {[
                          /* D'OÙ VIENT LE MONTANT. Un client qui monte
                             d'Essentiel à Pro lit « 93,43 € » alors que Pro est
                             affiché à 99 € : l'écart est le temps déjà payé sur
                             son forfait actuel, que Stripe lui rend. Sans ces
                             deux lignes, le total est incompréhensible — et un
                             montant incompréhensible se conteste. */
                          ...(confirmation.prorata_credit_eur
                            ? [[t('billing_detail_prorata_nouveau'), confirmation.prorata_debit_eur, false],
                               [t('billing_detail_prorata_credit'), -confirmation.prorata_credit_eur, false]]
                            : []),
                          [t('billing_detail_ht'), confirmation.montant_ht, false],
                          [confirmation.taux_tva != null
                            ? `${t('billing_detail_tva')} (${String(confirmation.taux_tva).replace('.', ',')} %)`
                            : t('billing_detail_tva'),
                           confirmation.montant_tva, false],
                          [t('billing_detail_total'), confirmation.montant_immediat, true],
                        ].map(([libelle, montant, total]) => (
                          <tr key={libelle} style={total ? { borderTop: '1px solid var(--gray)', fontWeight: 700 } : undefined}>
                            <td style={{ padding: '3px 24px 3px 0' }}>{libelle}</td>
                            <td style={{ padding: '3px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {(montant || 0).toFixed(2).replace('.', ',')} €
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {/* ⚠️ LA SEULE CONSÉQUENCE QUI TOUCHE D'AUTRES PERSONNES.
                  Quitter Cabinet referme la licence : les collègues qui y
                  occupent un siège perdent l'accès. En rouge, et non en ambre
                  comme les autres avertissements — ici ce n'est pas le
                  portefeuille du client qui est en jeu, c'est le travail de son
                  équipe. */}
              {confirmation.sieges_perdus > 0 && (
                <div style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(239,68,68,0.07)', border: '1px solid #fca5a5',
                  fontSize: 12.5, lineHeight: 1.55, color: 'var(--gray-dark)',
                }}>
                  <strong>{confirmation.sieges_perdus}</strong>{' '}
                  {confirmation.sieges_perdus > 1
                    ? t('billing_seats_lost_plural')
                    : t('billing_seats_lost_singular')}
                </div>
              )}
              {/* ⚠️ Une résiliation en cours sera levée par ce changement. Le
                  client n'a demandé qu'un forfait : reconduire l'abonnement
                  qu'il avait arrêté est une conséquence qu'il doit lire AVANT
                  de valider, pas découvrir au prélèvement suivant. */}
              {confirmation.resiliation_en_cours && (
                <div style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(245,158,11,0.09)', border: '1px solid #fcd34d',
                  fontSize: 12.5, lineHeight: 1.55, color: 'var(--gray-dark)',
                }}>
                  {t('billing_confirm_resume_warning')}
                </div>
              )}
            </>
          }
        />
      )}

      {pageIdentite && (
        <IdentiteFacturationModal
          identite={identite?.identite}
          emailConnexion={user?.email}
          mode={pageIdentite.priceId ? 'paiement' : 'completer'}
          onValide={identiteValidee}
          onClose={() => setPageIdentite(null)}
        />
      )}

      {banner && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 13, fontWeight: 600,
          background: bannerType === 'success' ? '#f0fdf4' : bannerType === 'error' ? '#fef2f2' : '#eff6ff',
          color: bannerType === 'success' ? '#16a34a' : bannerType === 'error' ? '#dc2626' : '#2563eb',
          border: `1px solid ${bannerType === 'success' ? '#86efac' : bannerType === 'error' ? '#fca5a5' : '#bfdbfe'}`,
        }}>
          {banner}
        </div>
      )}

      {/* Abonné en cours sans SIREN : INVITÉ, jamais bloqué (décision du
          22/09/2026). Le SIREN lui sera indispensable au 1er/09/2027. */}
      {hasActiveSubcription && identite?.concerne && !identite?.complete && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          padding: '12px 16px', borderRadius: 8, marginBottom: 20,
          background: 'rgba(245,158,11,0.09)', border: '1px solid #fcd34d',
        }}>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--gray-dark)' }}>
            <strong>{t('identite_bandeau_titre')}</strong><br />{t('identite_bandeau_texte')}
          </div>
          <Button size="sm" onClick={() => setPageIdentite({})}>{t('identite_bandeau_bouton')}</Button>
        </div>
      )}

      {/* Statut abonnement actuel */}
      {hasActiveSubcription ? (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{t('billing_current_plan')}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--black)' }}>
                {/* ⚠️ Repli sur `tierCode` : un compte encore rattaché à
                    PRO_PLUS (désactivé, non supprimé — cf révision 0013) doit
                    voir son code, pas un tiret. */}
                {LIBELLE_PALIER[tierCode] || tierCode || '—'}
              </div>
            </div>
            {/* Le fond suivait le statut, mais était figé en vert : un
                « Paiement en attente » orange s'affichait sur fond vert. */}
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 99,
              background: status === 'past_due' ? '#fffbeb'
                        : status === 'canceled' ? '#fef2f2' : '#f0fdf4',
              color: statusColor,
            }}>
              {statusLabel}
            </span>
          </div>
          {/* ⚠️ RÉSILIATION EN COURS — en tête, avant tout le reste. C'est le
              fait le plus important de l'écran : le client croit son forfait
              actif (il l'est, jusqu'au terme) et rien ne le lui disait, parce
              que la résiliation se fait depuis le portail Stripe et que le
              statut y reste `active`. Le bouton de reprise est ici, sous la
              date de fin, et non dans le portail : c'est là qu'il regarde. */}
          {resiliation && (
            <div style={{
              border: '1.5px solid #ef4444', borderRadius: 10,
              background: 'rgba(239,68,68,0.06)', padding: '12px 14px',
              fontSize: 13, color: 'var(--gray-dark)', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--black)', marginBottom: 4 }}>
                {t('billing_canceling_title')}{dateFin ? ` ${dateFin}` : ''}
              </div>
              <div style={{ marginBottom: 10 }}>{t('billing_canceling_body')}</div>
              <Button size="sm" disabled={subLoading} onClick={handleResume}>
                {t('billing_resume_cta')}
              </Button>
            </div>
          )}

          {/* Montée en gamme suspendue faute de paiement. DEUX issues, et les
              deux doivent être offertes : finir de payer, ou renoncer. Sans la
              seconde, la demande restait ouverte jusqu'à expiration. */}
          {enAttente && (
            <div style={{
              border: '1.5px solid #f59e0b', borderRadius: 10,
              background: 'rgba(245,158,11,0.07)', padding: '12px 14px',
              fontSize: 13, color: 'var(--gray-dark)', marginBottom: 16,
            }}>
              <div style={{ fontWeight: 600, color: 'var(--black)', marginBottom: 4 }}>
                {t('billing_pending_title')}{' '}
                <strong>{LIBELLE_PALIER[enAttente.tier_vise] || enAttente.tier_vise}</strong>
              </div>
              <div style={{ marginBottom: 10 }}>{t('billing_pending_body')}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(enAttente.client_secret || enAttente.url_paiement) && (
                  <Button size="sm" disabled={subLoading} onClick={handleFinaliserPaiement}>
                    {t('billing_pending_pay_cta')}
                  </Button>
                )}
                <Button variant="outline" size="sm"
                        onClick={handleAbandonPending} disabled={subLoading}>
                  {t('billing_pending_abandon_cta')}
                </Button>
              </div>
            </div>
          )}

          {/* Paiement en retard : le client garde son forfait pendant les
              relances, ses analyses sont suspendues (22/09/2026), et il doit
              pouvoir régulariser sans chercher. */}
          {status === 'past_due' && !enAttente && (
            <div style={{
              border: '1.5px solid #f59e0b', borderRadius: 10,
              background: 'rgba(245,158,11,0.07)', padding: '10px 14px',
              fontSize: 13, color: 'var(--gray-dark)', marginBottom: 16,
            }}>
              {t('billing_past_due_hint')}
            </div>
          )}
          {/* ⚠️ Masquée pendant une résiliation : l'encart ci-dessus porte déjà
              la date de fin, et « Se renouvelle le » la contredirait mot pour
              mot. Deux dates voisines qui se contredisent valent moins qu'une
              seule — d'autant que `cancel_at` et la fin de période peuvent ne
              pas tomber le même jour. */}
          {subData?.subscription_end_at && !resiliation && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              {status === 'canceled' ? t('billing_ends_on') : t('billing_renews_on')} {formatDate(subData.subscription_end_at)}
            </p>
          )}
          <Button variant="outline" onClick={handlePortal} disabled={subLoading}>
            {subLoading ? t('billing_redirecting') : t('billing_manage_cta')}
          </Button>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{t('billing_manage_hint')}</p>
        </Card>
      ) : (
        <Card style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)', border: '1.5px solid #bfdbfe' }}>
          {/* ⛔ Sans pictogramme : la page est un acte d'achat, pas une
              annonce. Les emoji y font perdre en sérieux — et sortent en
              carrés vides dès qu'un rendu PDF reprend ces libellés. */}
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)', marginBottom: 6 }}>
            {t('billing_onboarding_title')}
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>
            {t('billing_onboarding_body')}
          </p>
        </Card>
      )}

      {/* Cartes de plans */}
      {!stripeConfigured ? (
        <Card>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('billing_not_configured')}</p>
        </Card>
      ) : (
        <>
          {/* ⛔ Le sélecteur mensuel/annuel a été retiré le 08/09/2026 : tous
              les forfaits sont mensuels, sans engagement. */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {/* ⚠️ Trois cartes et non deux : `minWidth` force un retour à la
                ligne propre sous ~900 px, où trois colonnes `flex: 1` se
                seraient écrasées. */}
            {['ESSENTIEL', 'PRO', 'CABINET'].map(tier => (
              <div key={tier} style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 240 }}>
                <PlanCard
                  tier={tier}
                  isCurrentPlan={tierCode === tier && hasActiveSubcription}
                  billing={subData?.plans?.[tier]}
                  onSubscribe={handleSubscribe}
                  onKeepPlan={handleKeepPlan}
                  loading={subLoading}
                  etat={etatDeLaCarte(tier)}
                  dateBascule={dateBascule}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

// ─── Onglet Préférences ──────────────────────────────────────────────────────

const OptionCard = ({ value, current, onChange, icon, label, sub }) => (
  <div
    onClick={() => onChange(value)}
    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${current === value ? 'var(--blue)' : 'var(--gray)'}`, background: current === value ? 'rgba(59,130,246,0.08)' : 'var(--bg-surface)', cursor: 'pointer', transition: 'all 0.15s', marginBottom: 8 }}
  >
    <span style={{ fontSize: 20 }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{sub}</div>}
    </div>
    <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${current === value ? 'var(--blue)' : 'var(--gray)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {current === value && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--blue)' }} />}
    </div>
  </div>
);

// Préférences du COMPTE (révisions 0022 et 0023). ⚠️ Enregistrées sur le
// SERVEUR, à chaque clic — contrairement au thème et à la langue, gardés dans
// ce navigateur et validés par « Enregistrer ». Les mêler à ce bouton
// laisserait croire qu'un « Annuler » rétablit un choix déjà écrit en base.
// Elles suivent l'utilisateur d'un poste à l'autre.
// ⛔ Seuls les e-mails non obligatoires figurent ici : l'accusé de réception,
// les nouvelles CGV, le mot de passe et l'invitation ne se coupent pas.
const CaseEmail = ({ checked, disabled, onChange, label, sub }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, cursor: disabled ? 'default' : 'pointer' }}>
    <input type="checkbox" checked={!!checked} disabled={disabled}
           onChange={e => onChange(e.target.checked)} style={{ marginTop: 3 }} />
    <span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--black)', display: 'block' }}>{label}</span>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sub}</span>
    </span>
  </label>
);

const PreferencesCompte = () => {
  const { t } = useI18n();
  const [prefs, setPrefs] = useState(null);
  const [etat, setEtat] = useState('');       // '' | 'enregistre' | 'erreur'

  useEffect(() => {
    getPreferences().then(setPrefs).catch(() => setEtat('erreur'));
  }, []);

  const modifier = async (modification) => {
    const avant = prefs;
    setPrefs({ ...prefs, ...modification });
    setEtat('');
    try {
      setPrefs(await setPreferences(modification));
      setEtat('enregistre');
      setTimeout(() => setEtat(''), 2500);
    } catch (_) {
      setPrefs(avant);
      setEtat('erreur');
    }
  };

  const retour = (
    <>
      {etat === 'enregistre' && <div style={{ fontSize: 12, color: '#16a34a', marginTop: 8 }}>✅ {t('pref_emails_enregistre')}</div>}
      {etat === 'erreur' && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 8 }}>{t('err_generic')}</div>}
    </>
  );

  return (
    <>
      <Card>
        <SectionTitle>{t('pref_format_sortie')}</SectionTitle>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>{t('pref_format_sortie_sub')}</div>
        {[['both', 'output_both', 'output_both_desc'], ['html', 'output_html', 'output_html_desc'],
          ['excel', 'output_excel', 'output_excel_desc']].map(([valeur, libelle, detail]) => (
          <OptionCard key={valeur} value={valeur} current={prefs?.format_sortie_defaut}
                      onChange={v => prefs && modifier({ format_sortie_defaut: v })}
                      icon={valeur === 'excel' ? '📊' : valeur === 'html' ? '📄' : '📑'}
                      label={t(libelle)} sub={t(detail)} />
        ))}
      </Card>

      <Card>
        <SectionTitle>{t('pref_emails')}</SectionTitle>
        <CaseEmail checked={prefs?.email_fin_analyse} disabled={!prefs}
                   onChange={v => modifier({ email_fin_analyse: v })}
                   label={t('pref_email_fin_analyse')} sub={t('pref_email_fin_analyse_sub')} />
        <CaseEmail checked={prefs?.emails_consommation} disabled={!prefs}
                   onChange={v => modifier({ emails_consommation: v })}
                   label={t('pref_emails_consommation')} sub={t('pref_emails_consommation_sub')} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>{t('pref_emails_obligatoires')}</div>
      </Card>
      {retour}
    </>
  );
};

const PreferencesTab = () => {
  const { t, lang, setLang } = useI18n();
  const [theme, setThemeState]   = useState(localStorage.getItem('norma_theme') || 'light');
  const [language, setLanguage]  = useState(lang);
  const [saved, setSaved]        = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);

  const handleSave = () => {
    localStorage.setItem('norma_theme', theme);
    setLang(language);
    applyTheme(theme);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    const saved = localStorage.getItem('norma_theme') || 'light';
    setThemeState(saved);
    setLanguage(lang);
    applyTheme(saved);
  };

  return (
    <>
      <Card>
        <SectionTitle>{t('pref_theme')}</SectionTitle>
        <OptionCard value="light"  current={theme} onChange={setThemeState} icon="☀️" label={t('pref_light')}  sub={t('pref_light_sub')} />
        <OptionCard value="dark"   current={theme} onChange={setThemeState} icon="🌙" label={t('pref_dark')}   sub={t('pref_dark_sub')} />
        <OptionCard value="system" current={theme} onChange={setThemeState} icon="💻" label={t('pref_system')} sub={t('pref_system_sub')} />
      </Card>

      <Card>
        <SectionTitle>{t('pref_lang')}</SectionTitle>
        <OptionCard value="fr" current={language} onChange={setLanguage} icon="🇫🇷" label="Français" sub={t('pref_fr_sub')} />
        <OptionCard value="en" current={language} onChange={setLanguage} icon="🇬🇧" label="English"  sub={t('pref_en_sub')} />
      </Card>

      <PreferencesCompte />

      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="outline" onClick={handleReset}>{t('pref_cancel')}</Button>
        <Button onClick={handleSave}>
          {saved ? '✅ ' + t('save') : t('pref_save')}
        </Button>
      </div>
    </>
  );
};

// ─── Page principale Paramètres ──────────────────────────────────────────────

const Settings = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  // ⚠️ Règle partagée avec la barre latérale — cf `lib/acces.js`. Elle y vit
  // SEULE : écrite ici et là-bas, elle avait déjà divergé (le menu proposait
  // « Abonnement » à des comptes dont cet écran masquait l'onglet).
  const isEnterprise = !gereSonAbonnement(user);
  const [searchStr] = typeof window !== 'undefined'
    ? [window.location.search]
    : [''];
  const params    = new URLSearchParams(searchStr);
  const tabFromUrl = params.get('tab');

  const visibleTabs = TABS.filter(tab => !(tab === 'billing' && isEnterprise));

  const [activeTab, setActiveTab] = useState(
    visibleTabs.includes(tabFromUrl) ? tabFromUrl : 'profile'
  );

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const tab = p.get('tab');
    if (visibleTabs.includes(tab)) setActiveTab(tab);
  }, []);

  const tabLabels = {
    profile:     t('settings_tab_profile'),
    security:    t('settings_tab_security'),
    billing:     t('settings_tab_billing'),
    preferences: t('settings_tab_preferences'),
    privacy:     t('settings_tab_privacy'),
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f9fafb' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontFamily: 'Futura, Montserrat, sans-serif', fontSize: 24, fontWeight: 700, color: 'var(--black)', marginBottom: 28 }}>
          {t('settings_title')}
        </h1>

        {/* Onglets */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--gray)', marginBottom: 28, gap: 0 }}>
          {visibleTabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 20px', fontSize: 14, fontWeight: activeTab === tab ? 600 : 400,
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: activeTab === tab ? 'var(--blue)' : 'var(--text-muted)',
                borderBottom: activeTab === tab ? '2px solid var(--blue)' : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.15s',
              }}
            >
              {tabLabels[tab]}
            </button>
          ))}
        </div>

        {/* Contenu onglet */}
        {activeTab === 'profile'     && <ProfileTab />}
        {activeTab === 'security'    && <SecurityTab />}
        {activeTab === 'billing'     && !isEnterprise && <BillingTab />}
        {activeTab === 'preferences' && <PreferencesTab />}
        {activeTab === 'privacy'     && <PrivacyTab />}
      </div>
    </div>
  );
};

export default Settings;
